// Risk Combat Details and Move Analysis Component
import React, { useState } from 'react';
import { getTerritoryName } from '../maps_generated/risk/simple-map';

const RiskCombatDetails = ({ 
  turnDetails,
  stateBefore,
  stateAfter,
  currentPlayerAddress 
}) => {
  const [expandedCombat, setExpandedCombat] = useState(null);

  if (!turnDetails || !stateBefore || !stateAfter) {
    return null;
  }

  const combatEvents = turnDetails.events.filter(e => e.type === 'Combat');
  const conquestEvents = turnDetails.events.filter(e => e.type === 'TerritoryConquered');
  const turnSubmittedEvent = turnDetails.events.find(e => e.type === 'TurnSubmitted');

  // Analyze territorial changes
  const getTerritorialChanges = () => {
    const changes = [];
    for (let i = 0; i < 50; i++) {
      const beforeOwner = stateBefore.territoryOwners[i];
      const afterOwner = stateAfter.territoryOwners[i];
      const beforeUnits = stateBefore.territoryUnits[i];
      const afterUnits = stateAfter.territoryUnits[i];

      if (beforeOwner !== afterOwner) {
        changes.push({
          territory: i,
          type: 'conquest',
          from: beforeOwner,
          to: afterOwner,
          beforeUnits,
          afterUnits
        });
      } else if (beforeUnits !== afterUnits) {
        changes.push({
          territory: i,
          type: 'units_changed',
          owner: beforeOwner,
          from: beforeUnits,
          to: afterUnits,
          difference: afterUnits - beforeUnits
        });
      }
    }
    return changes;
  };

  // Analyze movement patterns from unit changes
  const getMovementAnalysis = () => {
    const movements = [];
    const playerTerritories = { 1: [], 2: [] };

    // Separate territories by player and identify unit changes
    for (let i = 0; i < 50; i++) {
      const owner = stateBefore.territoryOwners[i];
      const beforeUnits = stateBefore.territoryUnits[i];
      const afterUnits = stateAfter.territoryUnits[i];
      const unitChange = afterUnits - beforeUnits;

      // Only consider territories that didn't change ownership
      if (owner === stateAfter.territoryOwners[i] && owner > 0) {
        playerTerritories[owner].push({
          territory: i,
          beforeUnits,
          afterUnits,
          unitChange,
          name: getTerritoryName(i)
        });
      }
    }

    // Analyze unit flows for each player
    for (const [player, territories] of Object.entries(playerTerritories)) {
      const decreases = territories.filter(t => t.unitChange < 0);
      const increases = territories.filter(t => t.unitChange > 0);

      // Try to match decreases with increases to infer movements
      decreases.forEach(source => {
        const unitsLost = -source.unitChange;

        // Find potential destinations (increases) for this source
        const potentialDestinations = increases.filter(dest =>
          dest.unitChange > 0 && dest.unitChange <= unitsLost
        );

        if (potentialDestinations.length > 0) {
          // For simplicity, match with the first suitable destination
          const destination = potentialDestinations[0];
          movements.push({
            type: 'non_combat_move',
            player: parseInt(player),
            from: source.territory,
            to: destination.territory,
            fromName: source.name,
            toName: destination.name,
            units: Math.min(unitsLost, destination.unitChange),
            confidence: potentialDestinations.length === 1 ? 'high' : 'medium'
          });

          // Remove matched units from the destination to avoid double-counting
          destination.unitChange -= Math.min(unitsLost, destination.unitChange);
        }
      });
    }

    return movements;
  };

  // Analyze reinforcements (units that appeared without being moved)
  const getReinforcementAnalysis = () => {
    const reinforcements = [];
    const playerTotalChanges = { 1: 0, 2: 0 };

    // Calculate total unit changes per player
    for (let i = 0; i < 50; i++) {
      const owner = stateBefore.territoryOwners[i];
      const unitChange = stateAfter.territoryUnits[i] - stateBefore.territoryUnits[i];

      // Only count territories that remained under same ownership
      if (owner === stateAfter.territoryOwners[i] && owner > 0) {
        playerTotalChanges[owner] += unitChange;
      }
    }

    // Positive total changes indicate reinforcements
    for (const [player, totalChange] of Object.entries(playerTotalChanges)) {
      if (totalChange > 0) {
        reinforcements.push({
          player: parseInt(player),
          units: totalChange,
          type: 'reinforcement'
        });
      }
    }

    return reinforcements;
  };

  const territorialChanges = getTerritorialChanges();
  const movementAnalysis = getMovementAnalysis();
  const reinforcementAnalysis = getReinforcementAnalysis();

  // Format player display
  const formatPlayer = (playerNum) => {
    if (!playerNum) return 'Unknown';
    const isCurrentPlayer = (playerNum === 1 && stateBefore.player1 === currentPlayerAddress) ||
                           (playerNum === 2 && stateBefore.player2 === currentPlayerAddress);
    return `Player ${playerNum}${isCurrentPlayer ? ' (You)' : ''}`;
  };

  // Calculate combat effectiveness
  const getCombatStats = () => {
    let totalAttackers = 0;
    let totalDefenders = 0;
    let attackerCasualties = 0;
    let defenderCasualties = 0;
    
    combatEvents.forEach(combat => {
      totalAttackers += combat.args.attackers;
      totalDefenders += combat.args.defenders;
      attackerCasualties += combat.args.attackerLosses;
      defenderCasualties += combat.args.defenderLosses;
    });

    return {
      totalAttackers,
      totalDefenders,
      attackerCasualties,
      defenderCasualties,
      attackerEffectiveness: totalAttackers > 0 ? (defenderCasualties / totalAttackers * 100) : 0,
      defenderEffectiveness: totalDefenders > 0 ? (attackerCasualties / totalDefenders * 100) : 0
    };
  };

  const combatStats = getCombatStats();

  if (combatEvents.length === 0 && territorialChanges.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h3 className="text-xl font-semibold mb-3 text-gray-700">Turn Analysis</h3>
        <p className="text-gray-600">No combat or territorial changes in this turn.</p>
        {turnSubmittedEvent && (
          <p className="text-sm text-gray-500 mt-2">
            {formatPlayer(turnDetails.player)} submitted {turnSubmittedEvent.args.numMoves} moves (likely reinforcement only)
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <h3 className="text-xl font-semibold mb-4 text-gray-700">Turn Analysis</h3>
      
      {/* Turn Summary */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-semibold text-blue-800 mb-2">Turn {turnDetails.turnNumber} Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-blue-600">Player</p>
            <p className="font-medium">{formatPlayer(turnDetails.player)}</p>
          </div>
          <div>
            <p className="text-blue-600">Total Moves</p>
            <p className="font-medium">{turnSubmittedEvent?.args?.numMoves || 0}</p>
          </div>
          <div>
            <p className="text-blue-600">Combats</p>
            <p className="font-medium">{combatEvents.length}</p>
          </div>
          <div>
            <p className="text-blue-600">Territories Gained</p>
            <p className="font-medium">{conquestEvents.length}</p>
          </div>
        </div>
      </div>

      {/* Combat Statistics */}
      {combatEvents.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 rounded-lg">
          <h4 className="font-semibold text-red-800 mb-3">Combat Statistics</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-red-600">Total Attackers</p>
              <p className="font-medium text-lg">{combatStats.totalAttackers}</p>
            </div>
            <div>
              <p className="text-red-600">Total Defenders</p>
              <p className="font-medium text-lg">{combatStats.totalDefenders}</p>
            </div>
            <div>
              <p className="text-red-600">Attacker Losses</p>
              <p className="font-medium text-lg">{combatStats.attackerCasualties}</p>
            </div>
            <div>
              <p className="text-red-600">Defender Losses</p>
              <p className="font-medium text-lg">{combatStats.defenderCasualties}</p>
            </div>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-red-600 text-sm">Attacker Effectiveness</p>
              <div className="flex items-center mt-1">
                <div className="flex-1 bg-red-200 rounded-full h-3">
                  <div 
                    className="bg-red-500 h-3 rounded-full" 
                    style={{ width: `${Math.min(100, combatStats.attackerEffectiveness)}%` }}
                  ></div>
                </div>
                <span className="ml-2 text-sm font-medium">{combatStats.attackerEffectiveness.toFixed(1)}%</span>
              </div>
            </div>
            <div>
              <p className="text-red-600 text-sm">Defender Effectiveness</p>
              <div className="flex items-center mt-1">
                <div className="flex-1 bg-blue-200 rounded-full h-3">
                  <div 
                    className="bg-blue-500 h-3 rounded-full" 
                    style={{ width: `${Math.min(100, combatStats.defenderEffectiveness)}%` }}
                  ></div>
                </div>
                <span className="ml-2 text-sm font-medium">{combatStats.defenderEffectiveness.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Individual Combat Details */}
      {combatEvents.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Individual Combats</h4>
          <div className="space-y-2">
            {combatEvents.map((combat, idx) => {
              const territory = combat.args.territory;
              const isExpanded = expandedCombat === idx;
              const conquest = conquestEvents.find(c => c.args.territory === territory);
              
              return (
                <div key={idx} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => setExpandedCombat(isExpanded ? null : idx)}
                    className="w-full p-3 text-left hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-3 h-3 rounded-full ${conquest ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <div>
                        <p className="font-medium">
                          {getTerritoryName(territory)} {conquest ? '(Conquered)' : '(Defended)'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {combat.args.attackers} attackers vs {combat.args.defenders} defenders
                        </p>
                      </div>
                    </div>
                    <svg 
                      className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-200">
                      <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Attacking Force</p>
                          <p className="font-medium">{combat.args.attackers} units</p>
                          <p className="text-red-600">Lost: {combat.args.attackerLosses}</p>
                          <p className="text-green-600">Survived: {combat.args.attackers - combat.args.attackerLosses}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Defending Force</p>
                          <p className="font-medium">{combat.args.defenders} units</p>
                          <p className="text-red-600">Lost: {combat.args.defenderLosses}</p>
                          <p className="text-green-600">Survived: {combat.args.defenders - combat.args.defenderLosses}</p>
                        </div>
                      </div>
                      
                      {conquest && (
                        <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                          <p className="text-green-800 font-medium">Territory Conquered!</p>
                          <p className="text-green-700 text-sm">
                            {getTerritoryName(territory)} now controlled by {formatPlayer(turnDetails.player)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Territorial Changes Summary */}
      {territorialChanges.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Territorial Changes</h4>
          <div className="space-y-2">
            {territorialChanges.map((change, idx) => (
              <div key={idx} className="p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{getTerritoryName(change.territory)}</p>
                    {change.type === 'conquest' ? (
                      <p className="text-sm text-green-600">
                        Conquered by {formatPlayer(change.to)} (from {formatPlayer(change.from)})
                      </p>
                    ) : (
                      <p className="text-sm text-gray-600">
                        Units: {change.from} → {change.to} 
                        <span className={`ml-1 ${change.difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ({change.difference > 0 ? '+' : ''}{change.difference})
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Units After</p>
                    <p className="font-medium">{change.to || change.afterUnits}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Non-Combat Movements */}
      {movementAnalysis.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Non-Combat Movements</h4>
          <div className="space-y-2">
            {movementAnalysis.map((move, idx) => (
              <div key={idx} className="p-3 border border-gray-200 rounded-lg bg-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <div>
                      <p className="font-medium">
                        {move.fromName} → {move.toName}
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatPlayer(move.player)} moved {move.units} units
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Confidence</p>
                    <p className={`text-sm font-medium ${
                      move.confidence === 'high' ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {move.confidence}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reinforcements */}
      {reinforcementAnalysis.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Reinforcements</h4>
          <div className="space-y-2">
            {reinforcementAnalysis.map((reinforcement, idx) => (
              <div key={idx} className="p-3 border border-gray-200 rounded-lg bg-green-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <div>
                      <p className="font-medium">
                        {formatPlayer(reinforcement.player)} Reinforcements
                      </p>
                      <p className="text-sm text-gray-600">
                        +{reinforcement.units} units added to the battlefield
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Type</p>
                    <p className="text-sm font-medium text-green-600">New Units</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskCombatDetails;